import React from 'react';
import { ChevronDown, ChevronUp, Loader2, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { jobsApi, getErrorMessage } from '../../services/api';
import { formatINR } from '../command-center/constants';
import { useCanSeeRevenue } from '../../lib/perms';
import { toast } from 'sonner';

const METRIC_TABS = [
  { key: 'vacancies', label: 'Vacancies', accent: 'rose' },
  { key: 'active', label: 'Active Jobs', accent: 'sky' },
  { key: 'joined', label: 'Joined', accent: 'emerald' },
  { key: 'joined_ctc', label: 'Joined CTC', accent: 'teal' },
];

// Tailwind-safe colour ramps (must be static strings so JIT picks them up).
const COLOR_RAMPS = {
  rose: ['bg-white', 'bg-rose-50', 'bg-rose-100', 'bg-rose-200', 'bg-rose-300', 'bg-rose-400 text-white', 'bg-rose-500 text-white'],
  sky: ['bg-white', 'bg-sky-50', 'bg-sky-100', 'bg-sky-200', 'bg-sky-300', 'bg-sky-400 text-white', 'bg-sky-500 text-white'],
  emerald: ['bg-white', 'bg-emerald-50', 'bg-emerald-100', 'bg-emerald-200', 'bg-emerald-300', 'bg-emerald-400 text-white', 'bg-emerald-500 text-white'],
  teal: ['bg-white', 'bg-teal-50', 'bg-teal-100', 'bg-teal-200', 'bg-teal-300', 'bg-teal-400 text-white', 'bg-teal-600 text-white'],
};

const intensityClass = (value, max, accent) => {
  const ramp = COLOR_RAMPS[accent] || COLOR_RAMPS.sky;
  if (!value || max <= 0) return ramp[0];
  const ratio = value / max;
  // 6-step bucket. Anything >0 lands at step 1 minimum so cells never look empty.
  const idx = Math.min(ramp.length - 1, Math.max(1, Math.ceil(ratio * (ramp.length - 1))));
  return ramp[idx];
};

const ClientLocationHeatmap = ({
  onCellClick,
  // Optional: callback fired when a column header is clicked. When set,
  // the heatmap renders the header label as an interactive button instead
  // of a static <th>. Reports → Recruiter × Client uses this to navigate
  // to the matching client detail dialog.
  onColumnClick = null,
  period = 'all_time',
  // The following props let callers reuse this matrix for non-client
  // dimensions (e.g. recruiters) without forking the whole component.
  // Defaults preserve the original "Client × Location" behaviour used on
  // the Jobs page.
  loader = null,
  title = 'Client × Location',
  rowLabel = 'Client',
  rowNoun = 'clients',
  colNoun = 'locations',
  metricTabs = METRIC_TABS,
  defaultMetric = 'vacancies',
  testid = 'client-location-heatmap',
}) => {
  const canSeeRevenue = useCanSeeRevenue();
  // Non-admin roles never see revenue/CTC info. When we're showing the
  // default metric set (Vacancies / Active / Joined / Joined CTC), drop
  // "Joined CTC" for non-admin users. Callers that pass a custom
  // `metricTabs` (e.g. the Recruiter Revenue tab) are already gated at
  // the route level so we trust their tab list.
  const effectiveTabs = metricTabs === METRIC_TABS && !canSeeRevenue
    ? metricTabs.filter((t) => t.key !== 'joined_ctc')
    : metricTabs;
  const effectiveDefault = effectiveTabs.find((t) => t.key === defaultMetric)
    ? defaultMetric
    : (effectiveTabs[0]?.key || 'vacancies');
  const [collapsed, setCollapsed] = React.useState(false);
  const [metric, setMetric] = React.useState(effectiveDefault);
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);

  const load = React.useCallback(async (m, p) => {
    setLoading(true);
    try {
      const res = loader
        ? await loader(m, p)
        : await jobsApi.clientLocationMatrix(m, p);
      setData(res.data || null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load matrix'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [loader]);

  React.useEffect(() => {
    if (!collapsed) load(metric, period);
  }, [metric, period, collapsed, load]);

  const accent = effectiveTabs.find((t) => t.key === metric)?.accent || 'sky';
  const isCtcMetric = metric === 'joined_ctc';
  // Cells show counts for non-CTC metrics; for "Joined CTC" we render the
  // value as a compact ₹ amount (e.g. ₹2.50 Cr / ₹85.00 L) so the matrix
  // stays scannable even when totals run into crores.
  const displayCell = (v) => {
    if (!v) return '';
    if (!isCtcMetric) return v;
    const f = formatINR(v);
    return f ? `₹${f}` : '';
  };
  const clients = data?.clients || [];
  const locations = data?.locations || [];
  const matrix = data?.matrix || {};
  const rowTotals = data?.row_totals || {};
  const colTotals = data?.col_totals || {};
  const grand = data?.grand_total || 0;
  // Optional incentive math: when the backend echoes `incentive_pct_of_ctc`
  // (admin-configured), we render a "+ ₹X earned" pill next to row totals
  // and the grand total. Pure presentation — the rate itself stays admin-
  // editable on the Reports page.
  const incentivePct = isCtcMetric ? Number(data?.incentive_pct_of_ctc || 0) : 0;
  const earnedOf = (ctcRupees) => {
    if (!incentivePct || !ctcRupees) return 0;
    return ctcRupees * incentivePct / 100;
  };
  const displayEarned = (ctcRupees) => {
    const e = earnedOf(ctcRupees);
    if (e <= 0) return null;
    const f = formatINR(e);
    return f ? `+ ₹${f} earned` : null;
  };

  // Cap displayed locations for readability; keep "+N more" hint.
  const MAX_LOC = 12;
  const displayedLocations = locations.slice(0, MAX_LOC);
  const hiddenLocations = locations.slice(MAX_LOC);
  const hiddenLocCount = hiddenLocations.length;

  // Compute per-client "Other" cell totals = sum of HIDDEN locations.
  // Surfacing this as its own column makes the row totals reconcile with what
  // the user can actually see (no more "5 visible but Total=7" mystery).
  const otherByClient = {};
  let otherColTotal = 0;
  if (hiddenLocCount > 0) {
    Object.keys(matrix).forEach((cid) => {
      let s = 0;
      hiddenLocations.forEach((loc) => {
        s += matrix[cid]?.[loc] || 0;
      });
      if (s > 0) {
        otherByClient[cid] = s;
        otherColTotal += s;
      }
    });
  }

  // Find max cell value for colour intensity scaling (include "Other" so it scales right).
  let maxCell = 0;
  Object.values(matrix).forEach((row) => {
    Object.values(row).forEach((v) => { if (v > maxCell) maxCell = v; });
  });
  Object.values(otherByClient).forEach((v) => { if (v > maxCell) maxCell = v; });

  const isEmpty = !loading && grand === 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          {data ? (
            <span className="text-xs text-slate-500 truncate">
              · {clients.length} {clients.length === 1 ? rowNoun.replace(/s$/, '') : rowNoun} · {locations.length} {locations.length === 1 ? colNoun.replace(/s$/, '') : colNoun} · total {isCtcMetric ? (formatINR(grand) ? `₹${formatINR(grand)}` : '₹0') : grand}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!collapsed && (
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="heatmap-metric-tabs">
              {effectiveTabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setMetric(t.key)}
                  className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                    metric === t.key
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`heatmap-tab-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed((v) => !v)}
            className="h-8 px-2"
            data-testid="heatmap-toggle"
          >
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-3 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading matrix…
            </div>
          ) : isEmpty ? (
            <div className="text-center py-10 text-slate-400 text-sm" data-testid="heatmap-empty">
              No {metric === 'vacancies' ? 'open vacancies' : metric === 'active' ? 'active jobs' : metric === 'joined_ctc' ? 'joined CTC' : 'joined candidates'} to display.
            </div>
          ) : (
            <table className="text-xs border-collapse" data-testid="heatmap-table">
              <thead>
                <tr>
                  <th className="text-left text-[10px] uppercase tracking-wide text-slate-500 px-2 py-1.5 sticky left-0 bg-white z-10 min-w-[160px]">
                    {rowLabel}
                  </th>
                  {displayedLocations.map((loc) => (
                    <th
                      key={loc}
                      className={`text-center text-[10px] uppercase tracking-wide text-slate-500 px-2 py-1.5 min-w-[72px] ${onColumnClick ? 'cursor-pointer hover:bg-slate-50 hover:text-teal-700 transition' : ''}`}
                      data-testid={`heatmap-col-${loc}`}
                      title={onColumnClick ? `${loc} — click to open` : loc}
                      onClick={onColumnClick ? () => onColumnClick(loc) : undefined}
                    >
                      <div className={`truncate max-w-[88px] ${onColumnClick ? 'underline decoration-dotted underline-offset-2' : ''}`}>{loc}</div>
                    </th>
                  ))}
                  {hiddenLocCount > 0 && otherColTotal > 0 ? (
                    <th
                      className="text-center text-[10px] uppercase tracking-wide text-slate-500 px-2 py-1.5 min-w-[72px] bg-slate-50/50"
                      data-testid="heatmap-col-other"
                      title={`Aggregated from ${hiddenLocCount} hidden ${colNoun}: ${hiddenLocations.join(', ')}`}
                    >
                      <div className="truncate max-w-[88px]">Other ({hiddenLocCount})</div>
                    </th>
                  ) : null}
                  <th className="text-center text-[10px] uppercase tracking-wide text-slate-700 font-bold px-2 py-1.5 bg-slate-50">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id} data-testid={`heatmap-row-${c.id}`}>
                    <td className="text-left text-slate-700 font-medium px-2 py-1.5 sticky left-0 bg-white z-10 truncate max-w-[180px]" title={c.name}>
                      {c.name}
                    </td>
                    {displayedLocations.map((loc) => {
                      const v = matrix[c.id]?.[loc] || 0;
                      const isClickable = v > 0 && typeof onCellClick === 'function';
                      return (
                        <td
                          key={`${c.id}-${loc}`}
                          className={`text-center px-2 py-1.5 border border-slate-100 font-semibold text-slate-800 ${intensityClass(v, maxCell, accent)} ${isClickable ? 'cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-slate-400 transition-shadow' : ''}`}
                          data-testid={`heatmap-cell-${c.id}-${loc}`}
                          title={isClickable ? `Click to filter jobs · ${c.name} · ${loc}: ${displayCell(v) || v}` : `${c.name} · ${loc}: ${displayCell(v) || v}`}
                          onClick={isClickable ? () => onCellClick({ clientId: c.id, clientName: c.name, location: loc, metric, value: v }) : undefined}
                        >
                          {displayCell(v)}
                        </td>
                      );
                    })}
                    {hiddenLocCount > 0 && otherColTotal > 0 ? (
                      <td
                        className={`text-center px-2 py-1.5 border border-slate-100 font-semibold text-slate-800 ${intensityClass(otherByClient[c.id] || 0, maxCell, accent)}`}
                        data-testid={`heatmap-cell-other-${c.id}`}
                        title={otherByClient[c.id] ? `${c.name} · Other (${hiddenLocCount} hidden ${colNoun}): ${displayCell(otherByClient[c.id]) || otherByClient[c.id]}` : `${c.name} · No data in hidden ${colNoun}`}
                      >
                        {displayCell(otherByClient[c.id] || 0)}
                      </td>
                    ) : null}
                    <td className="text-center px-2 py-1.5 font-bold text-slate-900 bg-slate-50" data-testid={`heatmap-row-total-${c.id}`}>
                      <div className="flex flex-col items-center leading-tight">
                        <span>{isCtcMetric ? (displayCell(rowTotals[c.id] || 0) || '₹0') : (rowTotals[c.id] || 0)}</span>
                        {incentivePct > 0 && displayEarned(rowTotals[c.id] || 0) ? (
                          <span
                            className="mt-0.5 text-[9px] font-medium text-emerald-700"
                            data-testid={`heatmap-row-earned-${c.id}`}
                          >
                            {displayEarned(rowTotals[c.id] || 0)}
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-200 bg-slate-50">
                  <td className="text-left text-slate-700 font-bold px-2 py-1.5 sticky left-0 bg-slate-50 z-10">
                    Total
                  </td>
                  {displayedLocations.map((loc) => (
                    <td
                      key={`total-${loc}`}
                      className="text-center px-2 py-1.5 font-bold text-slate-900"
                      data-testid={`heatmap-col-total-${loc}`}
                    >
                      {isCtcMetric ? (displayCell(colTotals[loc] || 0) || '₹0') : (colTotals[loc] || 0)}
                    </td>
                  ))}
                  {hiddenLocCount > 0 && otherColTotal > 0 ? (
                    <td
                      className="text-center px-2 py-1.5 font-bold text-slate-900"
                      data-testid="heatmap-col-total-other"
                    >
                      {isCtcMetric ? (displayCell(otherColTotal) || '₹0') : otherColTotal}
                    </td>
                  ) : null}
                  <td className="text-center px-2 py-1.5 font-bold text-slate-900" data-testid="heatmap-grand-total">
                    <div className="flex flex-col items-center leading-tight">
                      <span>{isCtcMetric ? (displayCell(grand) || '₹0') : grand}</span>
                      {incentivePct > 0 && displayEarned(grand) ? (
                        <span
                          className="mt-0.5 text-[9px] font-medium text-emerald-700"
                          data-testid="heatmap-grand-earned"
                        >
                          {displayEarned(grand)}
                        </span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          {hiddenLocCount > 0 && !loading && !isEmpty ? (
            <div className="mt-2 text-[11px] text-slate-400">
              Showing top {MAX_LOC} of {locations.length} {colNoun}. {hiddenLocCount} more hidden.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ClientLocationHeatmap;
